// Maps between the IDP admin wire shapes and the users-feature domain types.
// This is the single boundary where the wire shape is known; everything above
// speaks domain types (mobile-app features/transform pattern).
import type { RegisterUserRequest, UpdateUserRequest, UserResponse } from '@/api/users.types'
import type { User, UserInput } from '../types'

export function toUser(response: UserResponse): User {
  const middleName = response.middleName ?? ''
  return {
    id: response.userId == null ? '' : String(response.userId),
    email: response.email,
    firstName: response.firstName,
    middleName,
    familyName: response.familyName,
    fullName: [response.firstName, middleName, response.familyName].filter((part) => part.trim() !== '').join(' '),
    active: response.isActive,
  }
}

// Create path: blanks become null, the contract's "absent" marker. The
// dashboard's user form no longer collects `position` (the IDP still owns
// the field for users provisioned elsewhere), so new users always start
// with it empty.
export function toRegisterRequest(input: UserInput): RegisterUserRequest {
  return {
    email: input.email,
    firstName: input.firstName,
    middleName: emptyToNull(input.middleName),
    familyName: input.familyName,
    password: emptyToNull(input.password),
    position: '',
    active: input.active,
  }
}

// Edit path: name + position only (the wire update request carries no
// email/password/active — those have their own endpoints). `position` is not
// part of the form; it is the value fetched alongside the user (see
// api.ts's getUser) so editing a user's name never wipes out a position set
// through another channel.
export function toUpdateRequest(input: UserInput, position: string): UpdateUserRequest {
  return {
    firstName: input.firstName,
    middleName: emptyToNull(input.middleName),
    familyName: input.familyName,
    position,
  }
}

// The form values for editing an existing user (email read-only, no password).
export function toInput(user: User): UserInput {
  return {
    email: user.email,
    firstName: user.firstName,
    middleName: user.middleName,
    familyName: user.familyName,
    password: '',
    active: user.active,
  }
}

function emptyToNull(value: string): string | null {
  return value.trim() === '' ? null : value
}
